/**
 * The four reports.
 *
 * Every figure is computed in the database and formatted here. Nothing on this
 * page adds anything up — a total assembled in the browser from a page of rows
 * is right until there are more rows than fit on a page.
 *
 * Each report reports **what it could not include**. A profitability figure
 * that quietly omits finance because the service is off is a figure somebody
 * will compare against last quarter's, so the absence is stated rather than
 * absorbed.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Clock, Receipt, TrendingUp, Users } from 'lucide-react';
import {
  Alert,
  api,
  Card,
  CardBody,
  CardHeader,
  cn,
  EmptyState,
  ErrorState,
  formatDate,
  formatMoney,
  Input,
  LoadingState,
  PageHeader,
  ScrollArea,
  StatusChip,
  Tabs,
  todayISO,
} from '@connect/web-shared';

import { PROJECT_STATUS_TONES } from '../../lib/tones';

type ReportId = 'time' | 'profitability' | 'receivables' | 'utilisation';

interface Unavailable {
  unavailable: string[];
  note?: string;
}

interface TimeRow {
  project_public_id: string;
  code: string;
  project_name: string;
  client_name: string;
  total_hours: string;
  billable_hours: string;
  billable_share: string;
  people: number;
}

interface ProfitRow {
  project_public_id: string;
  code: string;
  project_name: string;
  client_name: string;
  status: string;
  billing_type: string;
  currency: string;
  budget: string | null;
  booked_value: string;
  invoiced_value: string;
  unbilled: string;
}

interface ReceivableRow {
  client_public_id: string;
  client_name: string;
  currency: string;
  outstanding: string;
  current: string;
  overdue_1_30: string;
  overdue_31_60: string;
  overdue_60_plus: string;
  invoice_count: number;
}

interface UtilisationRow {
  employee_public_id: string;
  full_name: string;
  total_hours: string;
  billable_hours: string;
  utilisation: string;
}

const TABS = [
  { id: 'time', label: 'Time by project', icon: <Clock className="size-3.5" /> },
  { id: 'profitability', label: 'Profitability', icon: <TrendingUp className="size-3.5" /> },
  { id: 'receivables', label: 'Receivables', icon: <Receipt className="size-3.5" /> },
  { id: 'utilisation', label: 'Utilisation', icon: <Users className="size-3.5" /> },
] as const;

const PATHS: Record<ReportId, string> = {
  time: '/reports/time-by-project',
  profitability: '/reports/project-profitability',
  receivables: '/reports/receivables',
  utilisation: '/reports/utilisation',
};

/**
 * The last complete calendar month — the API's own default, mirrored here.
 *
 * Day **0** of this month is the last day of the previous one, which is how the
 * Date constructor is asked for it. Not by subtracting 86,400,000 from the
 * first of this month: a local day is 23 or 25 hours long either side of a
 * daylight-saving change, so a fixed 24 hours lands on the 30th in a 31-day
 * month and quietly drops its last day out of every report. India has no such
 * change and this installation would never have shown it.
 */
export function lastCompleteMonth(now: Date = new Date()): { from: string; to: string } {
  const lastOfPrevious = new Date(now.getFullYear(), now.getMonth(), 0);
  const firstOfPrevious = new Date(lastOfPrevious.getFullYear(), lastOfPrevious.getMonth(), 1);
  return { from: todayISO(firstOfPrevious), to: todayISO(lastOfPrevious) };
}

function Th({ children, numeric }: { children: React.ReactNode; numeric?: boolean }) {
  return (
    <th
      className={cn(
        'px-4 py-2 text-2xs font-medium uppercase tracking-wide text-faint',
        numeric ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  numeric,
  strong,
}: {
  children: React.ReactNode;
  numeric?: boolean;
  strong?: boolean;
}) {
  return (
    <td
      className={cn(
        'px-4 py-2.5 text-sm',
        numeric ? 'text-right tabular' : 'text-left',
        strong ? 'font-medium text-body' : 'text-muted',
      )}
    >
      {children}
    </td>
  );
}

export function ReportsPage() {
  const [tab, setTab] = useState<ReportId>('time');
  const [window, setWindow] = useState(lastCompleteMonth);

  const windowed = tab === 'time' || tab === 'utilisation';

  const report = useQuery({
    queryKey: ['reports', tab, windowed ? window : null],
    queryFn: () =>
      api.get<Record<string, unknown>>(PATHS[tab], {
        params: windowed ? { from: window.from, to: window.to } : undefined,
      }),
  });

  const data = report.data as (Unavailable & Record<string, unknown>) | undefined;
  const unavailable = data?.unavailable ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Reports"
        description="Computed in the database, over whatever services this installation has."
        tabs={
          <Tabs
            value={tab}
            onChange={(id) => setTab(id as ReportId)}
            items={TABS.map((t) => ({ id: t.id, label: t.label }))}
          />
        }
      />

      {windowed ? (
        <div className="flex flex-wrap items-end gap-3">
          <Input
            label="From"
            type="date"
            value={window.from}
            onChange={(event) => setWindow((w) => ({ ...w, from: event.target.value }))}
            containerClassName="w-40"
          />
          <Input
            label="To"
            type="date"
            value={window.to}
            onChange={(event) => setWindow((w) => ({ ...w, to: event.target.value }))}
            containerClassName="w-40"
          />
          <p className="pb-2 text-2xs text-faint">
            Defaults to the last complete month — a month-to-date figure compared against a full
            month is the commonest way a report misleads.
          </p>
        </div>
      ) : null}

      {unavailable.length > 0 ? (
        <Alert tone="warning" title="Part of this report is unavailable">
          {data?.note ??
            `Not enabled on this installation: ${unavailable.join(', ')}. The figures below exclude it.`}
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title={TABS.find((t) => t.id === tab)?.label ?? 'Report'}
          description={
            tab === 'utilisation' && typeof data?.note === 'string' ? data.note : undefined
          }
        />
        <CardBody className="p-0">
          {report.isLoading ? (
            <LoadingState label="Running the report" />
          ) : report.isError ? (
            <ErrorState onRetry={() => void report.refetch()} />
          ) : (
            <ScrollArea className="">
              {tab === 'time' ? <TimeTable rows={(data?.rows as TimeRow[]) ?? []} /> : null}
              {tab === 'profitability' ? (
                <ProfitTable rows={(data?.rows as ProfitRow[]) ?? []} />
              ) : null}
              {tab === 'receivables' ? (
                <ReceivablesTable
                  rows={(data?.rows as ReceivableRow[]) ?? []}
                  totals={data?.totals as Record<string, string> | null}
                  asOf={data?.as_of as string | undefined}
                />
              ) : null}
              {tab === 'utilisation' ? (
                <UtilisationTable rows={(data?.rows as UtilisationRow[]) ?? []} />
              ) : null}
            </ScrollArea>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function NoRows({ what }: { what: string }) {
  return (
    <EmptyState
      title="Nothing to report"
      description={what}
      icon={<BarChart3 className="size-5" />}
    />
  );
}

function TimeTable({ rows }: { rows: TimeRow[] }) {
  if (rows.length === 0) {
    return <NoRows what="No approved time was booked in this window. Only approved hours count — unapproved time is a number that can still change." />;
  }
  return (
    <table className="w-full">
      <thead className="border-b border-line">
        <tr>
          <Th>Project</Th>
          <Th>Client</Th>
          <Th numeric>Hours</Th>
          <Th numeric>Billable</Th>
          <Th numeric>Billable share</Th>
          <Th numeric>People</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.project_public_id} className="border-b border-line/60">
            <Td strong>{row.project_name}</Td>
            <Td>{row.client_name}</Td>
            <Td numeric strong>{row.total_hours}</Td>
            <Td numeric>{row.billable_hours}</Td>
            <Td numeric>{row.billable_share}%</Td>
            <Td numeric>{row.people}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProfitTable({ rows }: { rows: ProfitRow[] }) {
  if (rows.length === 0) return <NoRows what="No projects to report on." />;
  return (
    <table className="w-full">
      <thead className="border-b border-line">
        <tr>
          <Th>Project</Th>
          <Th>Client</Th>
          <Th>Status</Th>
          <Th numeric>Budget</Th>
          <Th numeric>Booked</Th>
          <Th numeric>Invoiced</Th>
          <Th numeric>Unbilled</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.project_public_id} className="border-b border-line/60">
            <Td strong>{row.project_name}</Td>
            <Td>{row.client_name}</Td>
            <Td>
              <StatusChip value={row.status} tones={PROJECT_STATUS_TONES} />
            </Td>
            <Td numeric>{formatMoney(row.budget, row.currency)}</Td>
            <Td numeric>{formatMoney(row.booked_value, row.currency)}</Td>
            {/* Net of tax: tax is collected on somebody else's behalf and is
                not revenue. Comparing it against booked time would overstate
                billing by the whole tax rate. */}
            <Td numeric>{formatMoney(row.invoiced_value, row.currency)}</Td>
            <Td numeric strong>
              <span className={Number(row.unbilled) > 0 ? 'text-warning' : undefined}>
                {formatMoney(row.unbilled, row.currency)}
              </span>
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReceivablesTable({
  rows,
  totals,
  asOf,
}: {
  rows: ReceivableRow[];
  totals: Record<string, string> | null | undefined;
  asOf?: string;
}) {
  if (rows.length === 0) {
    return <NoRows what="Nothing is outstanding. Every issued invoice has been settled." />;
  }
  const currency = rows[0]?.currency ?? 'INR';
  return (
    <>
      {asOf ? (
        <p className="px-4 pt-3 text-2xs text-faint">Aged as at {formatDate(asOf)}.</p>
      ) : null}
      <table className="w-full">
        <thead className="border-b border-line">
          <tr>
            <Th>Client</Th>
            <Th numeric>Invoices</Th>
            <Th numeric>Not yet due</Th>
            <Th numeric>1–30 days</Th>
            <Th numeric>31–60 days</Th>
            <Th numeric>60+ days</Th>
            <Th numeric>Outstanding</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.client_public_id} className="border-b border-line/60">
              <Td strong>{row.client_name}</Td>
              <Td numeric>{row.invoice_count}</Td>
              <Td numeric>{formatMoney(row.current, row.currency)}</Td>
              <Td numeric>{formatMoney(row.overdue_1_30, row.currency)}</Td>
              <Td numeric>
                <span className={Number(row.overdue_31_60) > 0 ? 'text-warning' : undefined}>
                  {formatMoney(row.overdue_31_60, row.currency)}
                </span>
              </Td>
              <Td numeric>
                <span className={Number(row.overdue_60_plus) > 0 ? 'text-danger' : undefined}>
                  {formatMoney(row.overdue_60_plus, row.currency)}
                </span>
              </Td>
              <Td numeric strong>{formatMoney(row.outstanding, row.currency)}</Td>
            </tr>
          ))}
        </tbody>
        {totals ? (
          <tfoot className="border-t border-line">
            <tr>
              <Td strong>Total</Td>
              <Td numeric>—</Td>
              <Td numeric strong>{formatMoney(totals.current, currency)}</Td>
              <Td numeric strong>{formatMoney(totals.overdue_1_30, currency)}</Td>
              <Td numeric strong>{formatMoney(totals.overdue_31_60, currency)}</Td>
              <Td numeric strong>{formatMoney(totals.overdue_60_plus, currency)}</Td>
              <Td numeric strong>{formatMoney(totals.outstanding, currency)}</Td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </>
  );
}

function UtilisationTable({ rows }: { rows: UtilisationRow[] }) {
  if (rows.length === 0) {
    return <NoRows what="No approved time in this window, so there is nothing to measure." />;
  }
  return (
    <table className="w-full">
      <thead className="border-b border-line">
        <tr>
          <Th>Who</Th>
          <Th numeric>Hours</Th>
          <Th numeric>Billable</Th>
          <Th numeric>Utilisation</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.employee_public_id} className="border-b border-line/60">
            <Td strong>{row.full_name}</Td>
            <Td numeric>{row.total_hours}</Td>
            <Td numeric>{row.billable_hours}</Td>
            <Td numeric strong>
              <span
                className={
                  Number(row.utilisation) >= 80
                    ? 'text-success'
                    : Number(row.utilisation) >= 60
                      ? 'text-warning'
                      : 'text-danger'
                }
              >
                {row.utilisation}%
              </span>
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
